import React, { useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Plus, Search, X } from "lucide-react";
import { SCIENCE_LINKS } from "../../content/siteContent";
import "./ScientistTeam.css";

const agents = [
  {
    id: "molecular_biologist",
    name: "Helix",
    role: "Molecular Biologist",
    image: "helix-molecular.png",
    width: 512,
    height: 512,
    output: "A target summary covering mechanisms, pathways and tissue context.",
    question: "Which tissues express this target, and what does that mean for our assay?",
  },
  {
    id: "computational_biologist",
    name: "Atlas",
    role: "Computational Biologist",
    image: "atlas-comp-bio.png",
    width: 602,
    height: 435,
    output: "A target assessment bringing together genetics, omics and disease evidence.",
    question: "Do human genetics and expression data support this target?",
  },
  {
    id: "statistician",
    name: "Sigma",
    role: "Biostatistician",
    image: "sigma-stats.png",
    width: 500,
    height: 500,
    output: "A statistical review of effect sizes, uncertainty, confounders and replication.",
    question: "How robust is this signal after accounting for confounders?",
  },
  {
    id: "protocol_designer",
    name: "Orin",
    role: "Protocol & Assay Designer",
    image: "orin-protocol.png",
    width: 256,
    height: 256,
    output: "An assay plan with proposed controls and experimental failure checks.",
    question: "What controls would make this assay result interpretable?",
  },
  {
    id: "medicinal_chemist",
    name: "Kestrel",
    role: "Medicinal Chemist",
    image: "kestrel-chemist.png",
    width: 256,
    height: 256,
    output: "A structure–activity review with chemical and ADMET liabilities to investigate.",
    question: "Which liabilities should we investigate before optimizing this series?",
  },
  {
    id: "oligo_designer",
    name: "Silex",
    role: "Oligo & siRNA Designer",
    image: "silex-oligo.png",
    width: 256,
    height: 256,
    output: "An oligo screening plan with off-target checks and knockdown readouts.",
    question: "How should we design the siRNA screen and confirm knockdown?",
  },
];

const connectorGroups = [
  { name: "Lab orchestration", systems: ["Biosero Green Button Go", "Cellario"] },
  { name: "Instruments", systems: ["Hamilton", "Tecan", "Opentrons", "SPT Labtech", "Beckman Coulter"] },
  { name: "Data & cloud", systems: ["Databricks", "Snowflake", "AWS", "Google Cloud", "Oracle"] },
  { name: "Lab records", systems: ["Benchling"] },
];

export default function ScientistTeam() {
  const location = useLocation();
  const [selected, setSelected] = useState(0);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const tabs = useRef([]);
  const integrationsToggle = useRef(null);
  const integrationSearch = useRef(null);
  const instanceId = useId();
  const titleId = `${instanceId}-title`;
  const detailId = `${instanceId}-detail`;
  const connectorTitleId = `${instanceId}-connectors`;
  const connectorPanelId = `${instanceId}-connector-panel`;
  const connectorSearchId = `${instanceId}-connector-search`;
  const active = agents[selected];
  const searchTerm = query.trim().toLocaleLowerCase();
  const visibleGroups = connectorGroups.map(({ name, systems }) => ({
    name,
    systems: systems.filter((system) => `${name} ${system}`.toLocaleLowerCase().includes(searchTerm)),
  })).filter(({ systems }) => systems.length > 0);
  const resultCount = visibleGroups.reduce((total, { systems }) => total + systems.length, 0);

  useEffect(() => {
    if (location.hash === "#integrations") setIntegrationsOpen(true);
  }, [location.hash, location.key]);

  const clearSearch = () => {
    setQuery("");
    integrationSearch.current?.focus();
  };

  const closeIntegrations = (event) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    setIntegrationsOpen(false);
    integrationsToggle.current?.focus();
  };

  const handleAgentKeys = (event, index) => {
    let next;
    if (event.key === "ArrowRight") next = (index + 1) % agents.length;
    if (event.key === "ArrowLeft") next = (index - 1 + agents.length) % agents.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = agents.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setSelected(next);
    tabs.current[next]?.focus();
  };

  return (
    <section className="scientist-team journey-shell" id="research-team" aria-labelledby={titleId}>
      <header className="scientist-team-heading">
        <h2 className="journey-title" id={titleId}>Specialists for <em>your science.</em></h2>
        <p>Software assistants for different scientific tasks, with outputs your team can inspect and review.</p>
      </header>

      <div className="scientist-team-studio">
        <div className="scientist-team-picker" role="tablist" aria-label="Choose a research agent">
          {agents.map((agent, index) => (
            <button
              className="scientist-team-person"
              type="button"
              role="tab"
              key={agent.id}
              id={`${instanceId}-${agent.id}`}
              aria-label={`${agent.name}, ${agent.role}`}
              aria-selected={selected === index}
              aria-controls={detailId}
              tabIndex={selected === index ? 0 : -1}
              ref={(element) => { tabs.current[index] = element; }}
              onKeyDown={(event) => handleAgentKeys(event, index)}
              onMouseEnter={() => setSelected(index)}
              onFocus={() => setSelected(index)}
              onClick={() => setSelected(index)}
            >
              <span className={`scientist-team-portrait${agent.width > agent.height ? " scientist-team-portrait-wide" : ""}`}>
                <img src={`/images/discovery/agents/${agent.image}`} alt="" width={agent.width} height={agent.height} loading="lazy" />
              </span>
              <span>{agent.name}</span>
            </button>
          ))}
        </div>

        <div className="scientist-team-detail" id={detailId} role="tabpanel" aria-labelledby={`${instanceId}-${active.id}`}>
          <div className="scientist-team-identity">
            <h3>{active.name}</h3>
            <p>{active.role}</p>
          </div>
          <div className="scientist-team-introduction">
            <p className="scientist-team-output-label">Intended output</p>
            <p className="scientist-team-purpose">{active.output}</p>
            <blockquote aria-label="Example question"><p>“{active.question}”</p></blockquote>

            <div className="scientist-team-action">
              <a href={`https://discovery.cornucopiabio.com/agents/${active.id}`} target="_blank" rel="noreferrer">
                Chat with {active.name} <ArrowUpRight aria-hidden="true" />
              </a>
              <span>Opens Discovery · sign-in required.</span>
            </div>
            <details className="scientist-agent-scope">
              <summary>How we specialize an agent <Plus size={15} aria-hidden="true" /></summary>
              <p>We scope the evidence sources, analysis tools and review checks for the task. A pilot tests that configuration against the baseline before its responsibilities expand.</p>
            </details>
          </div>
        </div>
      </div>

      <div className="scientist-team-connectors" id="integrations" aria-labelledby={connectorTitleId}>
        <h3 id={connectorTitleId}>
          <button type="button" className="scientist-integrations-toggle" ref={integrationsToggle} aria-expanded={integrationsOpen} aria-controls={connectorPanelId} onClick={() => setIntegrationsOpen(!integrationsOpen)}>
            {SCIENCE_LINKS.integrations.label} <ChevronDown aria-hidden="true" />
          </button>
        </h3>
        <div id={connectorPanelId} className="scientist-integrations-panel" hidden={!integrationsOpen} onKeyDown={closeIntegrations}>
          <div className="scientist-integrations-search">
            <Search size={18} aria-hidden="true" />
            <label className="sr-only" htmlFor={connectorSearchId}>Search integrations</label>
            <input id={connectorSearchId} ref={integrationSearch} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name or category" />
            {query && <button type="button" aria-label="Clear search" onClick={clearSearch}><X size={17} aria-hidden="true" /></button>}
          </div>
          <p className="scientist-integration-count" role="status">{resultCount} {resultCount === 1 ? "system" : "systems"} · Compatibility assessed per deployment</p>
          {resultCount > 0 ? (
            <div className="scientist-integrations-grid">
              {visibleGroups.map(({ name, systems }) => (
                <section className="scientist-connector-group" key={name} aria-label={name}>
                  <h4>{name}</h4>
                  <ul>{systems.map((system) => <li key={system}>{system}</li>)}</ul>
                </section>
              ))}
            </div>
          ) : (
            <div className="scientist-integrations-empty">
              <p>No systems match “{query.trim()}”.</p>
              <button type="button" onClick={clearSearch}>Show all systems</button>
            </div>
          )}
          <details className="scientist-integration-scope">
            <summary>Integration scope <Plus size={17} aria-hidden="true" /></summary>
            <p>Before a pilot, we confirm which connections can be reused and which need development. We agree on the device model, software version, available interfaces and actions to test.</p>
          </details>
        </div>
      </div>
    </section>
  );
}
